import { useTrans } from '@/shared/hooks/useTranslation';
import { Popover } from '@/view/shared/Popover';
import { CustomTooltip } from '@/view/shared/Tooltip';
import { AnnouncementList } from '@/view/shared/versionCheck/AnnouncementList';
import type { WhatsNewViewState } from '@/viewmodel/shared/versionCheck';
import { ExternalLink, Megaphone } from 'lucide-react';

export function WhatsNewButton({
  version,
  notes,
  hasUnseen,
  isOpen,
  changelogUrl,
  setOpen,
  close,
}: Readonly<WhatsNewViewState>) {
  const { t } = useTrans();

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={setOpen}>
      <CustomTooltip tip={t('whatsNew.title')} placement="bottom">
        <Popover.Trigger<'button'>
          render={(props) => (
            <button
              {...props}
              type="button"
              aria-label={t('whatsNew.title')}
              className="relative flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:bg-surface-secondary hover:text-foreground transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-border"
            >
              <Megaphone size={18} />
              {hasUnseen ? (
                <span
                  data-testid="whats-new-dot"
                  className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary ring-2 ring-surface"
                />
              ) : null}
            </button>
          )}
        />
      </CustomTooltip>

      <Popover.Content placement="bottom end">
        <Popover.Dialog
          aria-label={t('whatsNew.title')}
          className="w-88 max-w-[calc(100vw-2rem)] p-4"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">{t('whatsNew.title')}</h2>
              <span className="text-xs text-muted-foreground">{version}</span>
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <AnnouncementList notes={notes} />
            </div>

            <a
              href={changelogUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className="flex items-center justify-between gap-2 pt-3 border-t border-border text-sm text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-border rounded"
            >
              {t('whatsNew.seeAll')}
              <ExternalLink size={14} aria-hidden />
            </a>
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover.Root>
  );
}
